/**
 * usePushNotifications Composable
 *
 * @version 1.1.0
 * @description Web Push (VAPID) subscription lifecycle for the PWA.
 *              No Firebase / FCM SDK — speaks the W3C Web Push standard
 *              (RFC 8030 + RFC 8292) directly with the browser's PushManager.
 *
 * The browser picks its own push service (FCM for Chrome, Mozilla autopush
 * for Firefox, Apple Push for Safari) — we just sign requests with VAPID
 * and POST the resulting subscription to the backend.
 *
 * IMPORTANT — iOS:
 *   Web Push only works when the PWA is installed via "Add to Home Screen"
 *   on iOS 16.4+. In Mobile Safari the API exists but `requestPermission()`
 *   silently fails. Use `canPushOnIOS` to gate the UI.
 *
 * IMPORTANT — Subscription ↔ User binding (multi-user-per-device):
 *   Browser permission and the PushSubscription itself are per-origin (not
 *   per-user). The user↔endpoint binding lives in our DB. Two helper methods
 *   handle the rebinding silently across sessions:
 *
 *   - reattachIfPermitted()  — call on login/me() success. If the browser
 *     already has a PushSubscription and permission is granted, re-POSTs it to
 *     /push/subscribe so the row in `push_subscriptions` is reassigned to the
 *     currently authenticated user. No prompt, no UI — the user already
 *     consented at the device level.
 *
 *   - detachServerSide()  — call on logout (BEFORE the JWT is invalidated).
 *     DELETEs the row from `push_subscriptions` but leaves the browser-side
 *     PushSubscription intact. Result: the next user to log in on this device
 *     gets reattach() automatically (see above). No re-prompt needed.
 *
 * USAGE:
 *   const {
 *     isSupported,
 *     permission,
 *     isSubscribed,
 *     canPushOnIOS,
 *     iosNeedsInstall,
 *     subscribe,
 *     unsubscribe,
 *     sendTest,
 *     reattachIfPermitted,   // silent — call on login
 *     detachServerSide,      // silent — call on beforeLogout
 *   } = usePushNotifications()
 *
 *   // Toggle from a UI switch
 *   const onToggle = async (enabled: boolean) => {
 *     if (enabled) await subscribe()
 *     else await unsubscribe()
 *   }
 */

import { computed, ref, onMounted, type ComputedRef, type Ref } from 'vue'
import axios from 'axios'
import { usePwaDetection } from '@/composables/usePwaDetection'
import { useSnackbar } from '@/composables/useSnackbar'
import { standardizeErrorMessage } from '@/utils/utils'

// =============================================================================
// Types
// =============================================================================

export interface UsePushNotificationsReturn {
  /** Whether the runtime supports Service Worker + Push API */
  readonly isSupported: Ref<boolean>
  /** Current Notification permission state ('default' | 'granted' | 'denied') */
  readonly permission: Ref<NotificationPermission>
  /** Whether a PushSubscription exists in this browser */
  readonly isSubscribed: Ref<boolean>
  /** Whether a subscribe/unsubscribe operation is in flight */
  readonly isLoading: Ref<boolean>
  /** Convenience flag: device is iOS */
  readonly isIOS: ComputedRef<boolean>
  /** Convenience flag: iOS user must install the PWA before push will work */
  readonly iosNeedsInstall: ComputedRef<boolean>
  /** Whether push is reachable on the current device (false on iOS Safari, etc.) */
  readonly canPushOnIOS: ComputedRef<boolean>

  /** Request permission, subscribe via PushManager, POST to /push/subscribe */
  subscribe: () => Promise<boolean>
  /** Unsubscribe locally + DELETE on backend via /push/unsubscribe */
  unsubscribe: () => Promise<boolean>
  /** Trigger a server-side test push to the current user */
  sendTest: () => Promise<boolean>
  /** Re-read current subscription state from the SW registration */
  refreshState: () => Promise<void>
  /**
   * Silent reattach of the existing browser PushSubscription to the currently
   * authenticated user. No-op if browser has no subscription, no permission,
   * or push is unsupported. Never shows UI feedback.
   */
  reattachIfPermitted: () => Promise<boolean>
  /**
   * Silent server-side unregister of the current PushSubscription. DELETEs the
   * DB row but keeps the browser PushSubscription cached so the next user can
   * reattach without re-prompting. Never shows UI feedback. Must be called
   * BEFORE the JWT is invalidated by the /logout endpoint.
   */
  detachServerSide: () => Promise<boolean>
}

// =============================================================================
// Constants
// =============================================================================

const LOG_PREFIX = '[Push]'

// =============================================================================
// Helpers
// =============================================================================

/**
 * Convert a base64url-encoded string (as VAPID public keys are typically encoded)
 * to the Uint8Array required by `pushManager.subscribe({ applicationServerKey })`.
 */
function urlB64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

function detectIOS(): boolean {
  if (typeof navigator === 'undefined') return false
  // Modern detection: iPad reports as Mac with touch points on iPadOS 13+.
  // We accept the false-positive on Safari/Mac with touch — the actual push
  // gating happens via `isSupported` (Notification + PushManager) which is
  // available on macOS Safari too.
  const ua = navigator.userAgent
  return /iPad|iPhone|iPod/.test(ua)
    || (ua.includes('Mac') && navigator.maxTouchPoints > 1)
}

// =============================================================================
// Composable
// =============================================================================

export function usePushNotifications(): UsePushNotificationsReturn {
  const { isStandalone } = usePwaDetection()
  const { showSuccess, showError, showInfo } = useSnackbar()

  // ── Reactive state ────────────────────────────────────────────────────────

  const isSupported = ref<boolean>(
    typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
  )

  const permission = ref<NotificationPermission>(
    isSupported.value ? Notification.permission : 'denied'
  )

  const isSubscribed = ref<boolean>(false)
  const isLoading = ref<boolean>(false)

  const isIOS = computed(() => detectIOS())

  /**
   * On iOS, push only works when the PWA is installed (display-mode: standalone).
   * If we're on iOS *and* not standalone, the user must "Add to Home Screen" first.
   */
  const iosNeedsInstall = computed(() => isIOS.value && !isStandalone.value)

  /**
   * Whether push is actually usable here. On iOS, only when the PWA is installed.
   * On other platforms, isSupported is enough.
   */
  const canPushOnIOS = computed(() => {
    if (!isSupported.value) return false
    if (isIOS.value && !isStandalone.value) return false
    return true
  })

  // ── Internal: read current subscription from SW registration ──────────────

  async function _getActiveSubscription(): Promise<PushSubscription | null> {
    if (!isSupported.value) return null
    try {
      const reg = await navigator.serviceWorker.ready
      return await reg.pushManager.getSubscription()
    } catch (err) {
      console.warn(LOG_PREFIX, 'getSubscription failed:', err)
      return null
    }
  }

  async function refreshState(): Promise<void> {
    if (!isSupported.value) {
      isSubscribed.value = false
      return
    }
    permission.value = Notification.permission
    const sub = await _getActiveSubscription()
    isSubscribed.value = sub !== null
  }

  // ── Public: subscribe ─────────────────────────────────────────────────────

  async function subscribe(): Promise<boolean> {
    if (!isSupported.value) {
      showError('Le notifiche push non sono supportate su questo dispositivo.')
      return false
    }

    if (iosNeedsInstall.value) {
      showInfo(
        'Su iOS le notifiche push richiedono che la PWA sia installata. Aggiungi prima l\'app alla schermata Home.'
      )
      return false
    }

    isLoading.value = true

    try {
      // 1. Request permission (must be triggered by a user gesture)
      const perm = await Notification.requestPermission()
      permission.value = perm
      if (perm !== 'granted') {
        if (perm === 'denied') {
          showError('Permesso negato. Riabilita le notifiche dalle impostazioni del browser.')
        }
        return false
      }

      // 2. Fetch VAPID public key from backend
      const { data: vapid } = await axios.get<{ public_key: string }>(
        '/push/vapid-public-key'
      )
      if (!vapid?.public_key) {
        throw new Error('VAPID public key not returned by the server')
      }

      // 3. Get / create the PushSubscription
      const reg = await navigator.serviceWorker.ready
      let sub = await reg.pushManager.getSubscription()

      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true, // required by Chrome/Edge — must always show a notification
          // Cast: TS 5.7+ types Uint8Array as Uint8Array<ArrayBufferLike>, but the
          // PushSubscriptionOptions.applicationServerKey signature wants a BufferSource
          // backed by ArrayBuffer (not SharedArrayBuffer). Our helper always returns
          // a plain ArrayBuffer-backed Uint8Array, so the cast is safe.
          applicationServerKey: urlB64ToUint8Array(vapid.public_key) as BufferSource,
        })
      }

      // 4. POST the subscription to the backend
      await axios.post('/push/subscribe', sub.toJSON())

      isSubscribed.value = true
      showSuccess('Notifiche push attivate.')
      return true
    } catch (err) {
      console.error(LOG_PREFIX, 'subscribe failed:', err)
      const msg = standardizeErrorMessage(err) || 'Errore durante l\'attivazione delle notifiche push.'
      showError(msg)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // ── Public: unsubscribe (full — UI-driven, removes both DB row + browser sub) ─

  async function unsubscribe(): Promise<boolean> {
    if (!isSupported.value) return false
    isLoading.value = true

    try {
      const sub = await _getActiveSubscription()
      if (!sub) {
        isSubscribed.value = false
        return true
      }

      // Tell backend first — if the network call fails we still try local unsubscribe
      try {
        await axios.post('/push/unsubscribe', { endpoint: sub.endpoint })
      } catch (err) {
        console.warn(LOG_PREFIX, 'backend unsubscribe failed (continuing):', err)
      }

      const ok = await sub.unsubscribe()
      isSubscribed.value = !ok
      if (ok) showInfo('Notifiche push disattivate.')
      return ok
    } catch (err) {
      console.error(LOG_PREFIX, 'unsubscribe failed:', err)
      const msg = standardizeErrorMessage(err) || 'Errore durante la disattivazione delle notifiche.'
      showError(msg)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // ── Public: send a test push ──────────────────────────────────────────────

  async function sendTest(): Promise<boolean> {
    if (!isSubscribed.value) {
      showError('Devi prima attivare le notifiche push.')
      return false
    }
    isLoading.value = true
    try {
      await axios.post('/push/test')
      showSuccess('Notifica di test inviata. Dovresti riceverla a breve.')
      return true
    } catch (err) {
      console.error(LOG_PREFIX, 'sendTest failed:', err)
      const msg = standardizeErrorMessage(err) || 'Errore durante l\'invio della notifica di test.'
      showError(msg)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // ── Public: silent reattach of subscription ↔ user binding (on login) ────

  /**
   * Re-POST the existing browser PushSubscription to /push/subscribe so the
   * `push_subscriptions` row is reassigned to the currently authenticated user.
   *
   * Called silently after login and after `me()` on app boot. Idempotent: if
   * the row is already bound to the current user, the backend treats it as a
   * no-op (updateOrCreate by endpoint).
   *
   * No-op cases (all silent, no console.error):
   *   - Push API not supported
   *   - Browser permission != 'granted' (user never consented or revoked)
   *   - No PushSubscription cached in the SW registration
   *   - User not yet authenticated (no JWT → 401 → swallowed)
   *
   * @returns `true` if the reattach POST succeeded, `false` for any no-op or error.
   */
  async function reattachIfPermitted(): Promise<boolean> {
    if (!isSupported.value) return false
    if (Notification.permission !== 'granted') return false

    try {
      const sub = await _getActiveSubscription()
      if (!sub) {
        // Browser has no subscription cached — user must explicitly opt-in via subscribe()
        return false
      }

      await axios.post('/push/subscribe', sub.toJSON())
      isSubscribed.value = true
      console.log(LOG_PREFIX, 'subscription reattached to current user')
      return true
    } catch (err) {
      // Silent: this runs in the background on every login/refresh.
      // Failures are not blocking and the user can still use the app normally.
      console.warn(LOG_PREFIX, 'reattachIfPermitted failed (non-blocking):', err)
      return false
    }
  }

  // ── Public: silent server-side detach (on beforeLogout) ──────────────────

  /**
   * DELETE the `push_subscriptions` row for the current endpoint, but leave
   * the browser PushSubscription itself intact (cached in the SW registration).
   *
   * This is the recommended cleanup for logout in a multi-user-per-device
   * scenario: clearing the DB row stops notifications for the user who is
   * logging out, while the cached browser subscription allows the next user
   * to reattach silently via reattachIfPermitted() at login.
   *
   * MUST be called BEFORE the JWT is invalidated by /logout, otherwise the
   * /push/unsubscribe call returns 401 and the row is leaked. The auth store
   * `beforeLogout` hook fires at the right moment.
   *
   * @returns `true` if the row was successfully removed, `false` for no-op/error.
   */
  async function detachServerSide(): Promise<boolean> {
    if (!isSupported.value) return false
    try {
      const sub = await _getActiveSubscription()
      if (!sub) return false

      await axios.post('/push/unsubscribe', { endpoint: sub.endpoint })
      isSubscribed.value = false
      console.log(LOG_PREFIX, 'subscription detached server-side (browser sub preserved)')
      return true
    } catch (err) {
      // Silent: pre-logout cleanup is best-effort. If it fails (network down,
      // token already invalidated), the orphaned row will be auto-cleaned the
      // next time the push service returns 410 Gone for that endpoint.
      console.warn(LOG_PREFIX, 'detachServerSide failed (non-blocking):', err)
      return false
    }
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  onMounted(() => {
    void refreshState()
  })

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    isIOS,
    iosNeedsInstall,
    canPushOnIOS,
    subscribe,
    unsubscribe,
    sendTest,
    refreshState,
    reattachIfPermitted,
    detachServerSide,
  }
}

export default usePushNotifications
